import React, { useEffect } from 'react';
import { Button, Segmented, Select, Space } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import styles from './CalendarHeader.module.css';
import { useHistory } from 'react-router-dom';
import { useCalendars } from '../../contexts/calendar/CalendarsContext.context';
import { useCalendar } from '../../contexts/calendar/CalendarContext.context';
import { useCompany } from '../../contexts/CompanyContext';
import { useConfig } from '../../contexts/ConfigContext';
import { useUpdater } from '../../contexts/calendar/UpdateCalendarSelect.context';
import { CalendarService } from '../../services/calendar.api.client';
import { Calendar } from '../../models/calendar/Calendar.model';
import { mapToCalendar } from '../../models/calendar/Event.model';
import Cookies from 'js-cookie';

dayjs.locale('es');

interface CalendarHeaderProps {
  view: string;
  day: string;
  month: string;
  year: string;
}

const CalendarHeader: React.FC<CalendarHeaderProps> = ({ view, day, month, year }) => {
  const history = useHistory();
  const { calendars, setCalendars } = useCalendars();
  const { calendar, setCalendar } = useCalendar();
  const { company } = useCompany();
  const { config } = useConfig();
  const { updater } = useUpdater();

  useEffect(() => {
    if (!company.current.uid || !config.key) return;
    
    // Fetch calendars if empty — only after auth key is ready
    if (calendars.length === 0) {
      CalendarService.getCalendarByCompany(company.current.uid)
        .then((data: Calendar[]) => {
          if (Array.isArray(data)) {
            setCalendars(data.map(c => ({...c, ...mapToCalendar(c)})));
          }
        });
    }
  }, [company.current.uid, config.key, updater]);

  useEffect(() => {
    if (!calendar.uid && calendars.length > 0) {
      // If we have calendars but none selected, select the first one
      selectCalendar(calendars[0]);
    }
  }, [calendars, calendar.uid]);

  const selectCalendar = (selected: Calendar) => {
    const weekrules = [false, false, false, false, false, false, false].map((_, i) => (
      selected.weekAvailable.some((day) => day.numberDay === i)
    ));
    
    setCalendar((prev) => ({
      ...prev,
      id: selected.id,
      uid: selected.uid,
      color: selected.color,
      availableWeekRules: weekrules,
      weekAvailable: selected.weekAvailable,
      customEvents: selected.customEvents,
      category: selected.category
    }));

    Cookies.set("calendar_id", selected.id, {
      expires: 1 / 24,
      path: "/",
    });

    history.push(`/calendar/${view}/${year}/${Number.parseInt(month) + 1}/${day}?calendar=${selected.uid}`);
  };

  const currentDayjs = dayjs(new Date(Number.parseInt(year), Number.parseInt(month), Number.parseInt(day)));

  const navigate = (direction: number) => {
    let nextDate = currentDayjs;
    if (view === 'day') {
      nextDate = currentDayjs.add(direction, 'day');
    } else if (view === 'week') {
      nextDate = currentDayjs.add(direction, 'week');
    } else if (view === 'month') {
      nextDate = currentDayjs.add(direction, 'month');
    }
    
    updateUrl(view, nextDate);
  };

  const handleToday = () => {
    updateUrl(view, dayjs());
  };

  const handleViewChange = (newView: string) => {
    updateUrl(newView, currentDayjs);
  };

  const updateUrl = (v: string, date: dayjs.Dayjs) => {
    // month + 1 because the URL expects 1-indexed month
    history.push(`/calendar/${v}/${date.year()}/${date.month() + 1}/${date.date()}?calendar=${calendar.uid}`);
  };

  const handleCalendarChange = (value: string) => {
    const selected = calendars.find(c => c.uid === value);
    if (selected) {
      selectCalendar(selected);
    }
  };

  const getTitle = () => {
    if (view === 'day' || view === 'week') {
      return currentDayjs.format('MMMM D, YYYY');
    }
    return currentDayjs.format('MMMM YYYY');
  };

  return (
    <header className={styles.calendarHeader}>
      <div className={styles.leftSection}>
        <Button onClick={handleToday}>Hoy</Button>
        <Space>
          <Button 
            icon={<LeftOutlined />} 
            onClick={() => navigate(-1)}
            shape="circle"
          />
          <Button 
            icon={<RightOutlined />} 
            onClick={() => navigate(1)}
            shape="circle"
          />
        </Space>
        <span className={styles.currentDate}>{getTitle()}</span>
      </div>

      <div className={styles.rightSection}>
        <Select
          className={styles.calendarSelect}
          value={calendar.uid}
          onChange={handleCalendarChange}
          options={calendars.map(c => ({ label: c.name, value: c.uid }))}
          placeholder="Seleccionar Calendario"
        />
        <Segmented
          options={[
            { label: 'Día', value: 'day' },
            { label: 'Semana', value: 'week' },
            { label: 'Mes', value: 'month' },
          ]}
          value={view}
          onChange={(value) => handleViewChange(value)}
        />
      </div>
    </header>
  );
};

export default CalendarHeader;
